import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import {
  getLeads,
  AdminLeadDTO,
  LeadListParams,
  LeadStatus,
  LastInboundAction,
  LinkDelivery,
} from "../../services/LeadsService";
import UserCell from "../../components/UserCell";
import DateRangePicker from "../../components/DateRangePicker";
import LeadActions from "./LeadActions";
import AmountCell from "./AmountCell";

type BucketKey = "callbacks" | "cold" | "needs_delivery" | "closed" | "all";

const PAGE_SIZE = 15;

const BUCKETS: { key: BucketKey; label: string }[] = [
  { key: "callbacks", label: "Callbacks" },
  { key: "cold", label: "Cold" },
  { key: "needs_delivery", label: "Needs delivery" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

// Bucket → the filter preset it applies over the list endpoint.
const bucketFilters = (
  bucket: BucketKey,
  closedStatus: "FULFILLED" | "IGNORED"
): Partial<LeadListParams> => {
  switch (bucket) {
    case "callbacks":
      return { status: "CREATED", lastInboundAction: "TALK_TO_COACH" };
    case "cold":
      return { status: "CREATED", hasInboundAction: false };
    case "needs_delivery":
      return { status: "LINK_CREATED", needsDelivery: true };
    case "closed":
      return { status: closedStatus };
    case "all":
    default:
      return {};
  }
};

const statusChipColor = (
  status: LeadStatus
): "default" | "info" | "warning" | "success" => {
  switch (status) {
    case "FULFILLED":
      return "success";
    case "LINK_CREATED":
      return "warning";
    case "IGNORED":
      return "default";
    case "CREATED":
    default:
      return "info";
  }
};

const STATUS_LABEL: Record<LeadStatus, string> = {
  CREATED: "Created",
  LINK_CREATED: "Link created",
  FULFILLED: "Fulfilled",
  IGNORED: "Ignored",
};

const INBOUND_LABEL: Record<Exclude<LastInboundAction, null>, string> = {
  PURCHASE: "Purchase",
  TALK_TO_COACH: "Talk to coach",
  EXPLORE: "Explore",
};

const DELIVERY_LABEL: Record<Exclude<LinkDelivery, null>, string> = {
  SENT_AUTO: "Sent (auto)",
  PENDING_MANUAL: "Needs delivery",
  SENT_MANUAL: "Sent (manual)",
  FAIL: "Send failed",
};

const deliveryChipColor = (
  delivery: Exclude<LinkDelivery, null>
): "info" | "warning" | "success" | "error" => {
  switch (delivery) {
    case "SENT_AUTO":
      return "success";
    case "PENDING_MANUAL":
      return "warning";
    case "FAIL":
      return "error";
    case "SENT_MANUAL":
    default:
      return "info";
  }
};

// Compact relative age from an ISO timestamp — "2 min", "3 h", "5 d".
const formatAge = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "N/A";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h`;
  const days = Math.floor(hrs / 24);
  return `${days} d`;
};

const LeadsList = (): JSX.Element => {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [leads, setLeads] = useState<AdminLeadDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [bucket, setBucket] = useState<BucketKey>("callbacks");
  const [closedStatus, setClosedStatus] = useState<"FULFILLED" | "IGNORED">(
    "FULFILLED"
  );

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Monotonic request id — filters/tabs/pages fire overlapping getLeads calls;
  // only the latest may commit, so a slow older response can't overwrite a newer
  // bucket's rows and counts.
  const requestId = useRef(0);

  // The live query, refreshed every render. fetchLeads reads it (not closure
  // state), so a refetch triggered by a late action completion — whose captured
  // closure may hold an older bucket — still fetches the CURRENT bucket.
  const queryRef = useRef<LeadListParams | null>(null);
  queryRef.current = {
    page: currentPage,
    size: PAGE_SIZE,
    ...bucketFilters(bucket, closedStatus),
    search: search || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, closedStatus, search, startDate, endDate, currentPage]);

  const fetchLeads = async () => {
    const reqId = ++requestId.current;
    setLoading(true);
    setError(false);
    try {
      const data = await getLeads(queryRef.current as LeadListParams);
      if (reqId !== requestId.current) return; // a newer request superseded this one
      setLeads(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch {
      if (reqId !== requestId.current) return;
      setError(true);
      setLeads([]);
    } finally {
      if (reqId === requestId.current) setLoading(false);
    }
  };

  const handleBucketChange = (_e: React.SyntheticEvent, next: BucketKey) => {
    setBucket(next);
    setCurrentPage(0);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(0);
  };

  const hasActiveFilters = search || startDate || endDate;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Buckets */}
      <Tabs
        value={bucket}
        onChange={handleBucketChange}
        sx={{ mb: 2, minHeight: "auto" }}
      >
        {BUCKETS.map((b) => (
          <Tab
            key={b.key}
            value={b.key}
            label={b.label}
            sx={{ minWidth: "initial" }}
          />
        ))}
      </Tabs>

      {/* Filters */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <TextField
          placeholder="Search by name or phone"
          size="small"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="contained" size="small" onClick={handleSearch}>
          Search
        </Button>

        {bucket === "closed" && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Outcome</InputLabel>
            <Select
              value={closedStatus}
              label="Outcome"
              onChange={(e) => {
                setClosedStatus(e.target.value as "FULFILLED" | "IGNORED");
                setCurrentPage(0);
              }}
            >
              <MenuItem value="FULFILLED">Fulfilled</MenuItem>
              <MenuItem value="IGNORED">Ignored</MenuItem>
            </Select>
          </FormControl>
        )}

        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={({ startDate: nextStart, endDate: nextEnd }) => {
            setStartDate(nextStart);
            setEndDate(nextEnd);
            setCurrentPage(0);
          }}
        />

        {hasActiveFilters && (
          <Button
            variant="outlined"
            size="small"
            onClick={clearFilters}
            startIcon={<ClearIcon />}
          >
            Clear
          </Button>
        )}
      </Box>

      {/* Table */}
      <TableContainer sx={{ flex: 1, scrollbarWidth: "none" }}>
        <Table
          stickyHeader
          sx={{
            tableLayout: "fixed",
            minWidth: 1000,
            "& th": { fontWeight: 600 },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.info.light }}>
              <TableCell sx={{ width: 210 }}>Customer</TableCell>
              <TableCell sx={{ width: 130 }}>Program</TableCell>
              <TableCell sx={{ width: 110 }} align="center">
                Status
              </TableCell>
              <TableCell sx={{ width: 130 }} align="center">
                Last action
              </TableCell>
              <TableCell sx={{ width: 150 }} align="center">
                Delivery
              </TableCell>
              <TableCell sx={{ width: 70 }} align="center">
                Age
              </TableCell>
              <TableCell sx={{ width: 90 }} align="right">
                Amount
              </TableCell>
              <TableCell sx={{ width: 240 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <SkeletonRows />
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box sx={{ py: 4 }}>
                    <Typography color="error" gutterBottom>
                      Couldn&apos;t load leads.
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={fetchLeads}
                    >
                      Retry
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary" sx={{ py: 4 }}>
                    No leads found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>
                    <UserCell user={lead.user} />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {lead.programName || "N/A"}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label={STATUS_LABEL[lead.status]}
                      color={statusChipColor(lead.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell align="center">
                    {lead.lastInboundAction ? (
                      <Chip
                        label={INBOUND_LABEL[lead.lastInboundAction]}
                        color={
                          lead.lastInboundAction === "TALK_TO_COACH"
                            ? "warning"
                            : "default"
                        }
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {lead.linkDelivery ? (
                      <Box>
                        <Chip
                          label={DELIVERY_LABEL[lead.linkDelivery]}
                          color={deliveryChipColor(lead.linkDelivery)}
                          size="small"
                          variant="outlined"
                        />
                        {lead.status === "LINK_CREATED" &&
                          lead.link &&
                          !lead.link.linkLive && (
                            <Typography
                              variant="caption"
                              color="error"
                              display="block"
                            >
                              link expired
                            </Typography>
                          )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {formatAge(lead.createdAt)}
                  </TableCell>

                  <TableCell align="right">
                    <AmountCell orderId={lead.orderId} />
                  </TableCell>

                  <TableCell align="center">
                    <LeadActions lead={lead} onChanged={fetchLeads} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
        }}
      >
        <Typography variant="body2">
          Total: <b>{totalElements}</b> | Page <b>{currentPage + 1}</b> of{" "}
          {totalPages || 1}
        </Typography>
        <Box>
          <Button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 0}
          >
            Previous
          </Button>
          <Button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const SkeletonRows = () => (
  <>
    {[...Array(5)].map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Skeleton height={35} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={25} />
        </TableCell>
        <TableCell>
          <Skeleton height={35} />
        </TableCell>
      </TableRow>
    ))}
  </>
);

export default LeadsList;
